"use client";

import Link from "next/link";
import { ChevronLeft, ChevronRight } from "@/components/icons";
import { motion, useReducedMotion } from "@/lib/motion";

function getPageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "…", total];
  if (current >= total - 3) return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "…", current - 1, current, current + 1, "…", total];
}

interface PaginationProps {
  current: number;
  total: number;
  onChange: (page: number) => void;
  /**
   * When provided, page controls render as real <Link> anchors (crawlable,
   * work without JS) instead of onClick-only buttons. Use for the default,
   * unfiltered archive view so search engines can follow pagination.
   */
  hrefFor?: (page: number) => string;
}

const btnBase =
  "flex items-center justify-center w-11 h-11 rounded-xl text-sm font-medium transition-all duration-150 select-none border";
const btnEnabled =
  "border-border bg-card text-muted-foreground hover:text-foreground hover:border-brand/40 hover:bg-brand/5";
const btnDisabled = "opacity-30 cursor-not-allowed border-border bg-card text-muted-foreground";

export function Pagination({ current, total, onChange, hrefFor }: PaginationProps) {
  const prefersReducedMotion = useReducedMotion();

  if (total <= 1) return null;

  const pages = getPageWindow(current, total);

  return (
    <motion.nav
      initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="mt-12 flex items-center justify-center gap-2"
      aria-label="Pagination"
    >
      {hrefFor && current > 1 ? (
        <Link
          href={hrefFor(current - 1)}
          aria-label="Previous page"
          className={`${btnBase} ${btnEnabled}`}
        >
          <ChevronLeft size={15} />
        </Link>
      ) : (
        <button
          onClick={() => onChange(current - 1)}
          disabled={current === 1}
          aria-label="Previous page"
          className={`${btnBase} ${current === 1 ? btnDisabled : btnEnabled}`}
        >
          <ChevronLeft size={15} />
        </button>
      )}

      {(() => {
        let ellipsisCount = 0;
        return pages.map((p) => {
          if (p === "…") {
            return (
              <span
                key={`ellipsis-${++ellipsisCount}`}
                className="text-muted-foreground flex h-11 w-11 items-center justify-center text-base select-none"
                aria-hidden="true"
              >
                —
              </span>
            );
          }

          const isCurrent = p === current;
          const pageClass = `${btnBase} ${
            isCurrent
              ? "border-brand/60 bg-brand/10 text-foreground font-semibold"
              : "border-border bg-card text-muted-foreground hover:text-foreground hover:border-brand/30 hover:bg-brand/5"
          }`;

          if (hrefFor) {
            return (
              <Link
                key={p}
                href={hrefFor(p)}
                aria-label={`Page ${p}`}
                aria-current={isCurrent ? "page" : undefined}
                className={pageClass}
              >
                {p}
              </Link>
            );
          }

          return (
            <button
              key={p}
              onClick={() => onChange(p)}
              aria-label={`Page ${p}`}
              aria-current={isCurrent ? "page" : undefined}
              className={pageClass}
            >
              {p}
            </button>
          );
        });
      })()}

      {hrefFor && current < total ? (
        <Link
          href={hrefFor(current + 1)}
          aria-label="Next page"
          className={`${btnBase} ${btnEnabled}`}
        >
          <ChevronRight size={15} />
        </Link>
      ) : (
        <button
          onClick={() => onChange(current + 1)}
          disabled={current === total}
          aria-label="Next page"
          className={`${btnBase} ${current === total ? btnDisabled : btnEnabled}`}
        >
          <ChevronRight size={15} />
        </button>
      )}
    </motion.nav>
  );
}
